/**
 * imgproxy URLs, built against the tcgfan-backend `/img` endpoint.
 *
 * The backend holds IMGPROXY_KEY / IMGPROXY_SALT and 302s to the signed
 * imgproxy URL. That means these URLs are built synchronously here — no
 * round trip, no signing secret in this app, and the redirect itself is
 * cached at Cloudflare's edge.
 *
 * Shape: <API_URL>/img/<option>/<option>/<base64url-source>.<ext>
 */

/**
 * NEXT_PUBLIC_IMG_URL already points at the backend's signing endpoint
 * (https://img.tcgfan.com/img), so it includes the /img prefix.
 */
const IMG_URL = (
  process.env.NEXT_PUBLIC_IMG_URL || "https://img.tcgfan.com/img"
).replace(/\/+$/, "");

/**
 * The backend only signs sources under these prefixes (IMGPROXY_ALLOWED_SOURCES).
 * Anything else 403s, so it is passed through untouched rather than turned into
 * a URL we know will fail. Everything now lives on cdn.tcgfan.com; the backend
 * still allows the retired cdn.gumgum.gg / cdn2.tcgfan.com hosts, but nothing
 * here emits them.
 */
const ALLOWED_SOURCE_PREFIXES = ["https://cdn.tcgfan.com/"];

/**
 * Base64url, working in both the browser and Node (SSR/RSC).
 *
 * btoa is preferred over Buffer: bundlers polyfill Buffer for the browser, but
 * that polyfill does not implement the "base64url" encoding and throws on it —
 * so `typeof Buffer !== "undefined"` is not a safe test for real Node.
 */
function encodeBase64Url(value: string): string {
  // btoa is latin1-only, so UTF-8 encode first.
  const bytes = new TextEncoder().encode(value);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);

  if (typeof btoa === "function") {
    return btoa(binary)
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");
  }

  return Buffer.from(value, "utf8").toString("base64url");
}

export interface ImgproxyOptions {
  width?: number;
  height?: number;
  /** imgproxy resize type. "fit" letterboxes, "fill" crops to fill. */
  resize?: "fit" | "fill" | "auto";
  /** Do not upscale images smaller than the requested box. */
  enlarge?: boolean;
  quality?: number;
  /** Output format. "webp" is a safe default for every browser we support. */
  format?: "webp" | "avif" | "png" | "jpg";
}

export function isImgproxyEligible(src: string): boolean {
  return ALLOWED_SOURCE_PREFIXES.some((prefix) => src.startsWith(prefix));
}

/**
 * Build a backend-signed imgproxy URL for `src`.
 *
 * Sources the backend will not sign are returned unchanged, so callers can use
 * this unconditionally without checking the host first.
 */
export function getImgproxyUrl(
  src: string,
  {
    width,
    height,
    resize = "fit",
    enlarge = false,
    quality = 82,
    format = "webp",
  }: ImgproxyOptions = {},
): string {
  if (!src || !isImgproxyEligible(src)) return src;

  // The browser used to percent-encode these on its own when the URL was an
  // img src; inside the base64 payload imgproxy would receive them raw.
  const source = src.replace(/ /g, "%20");

  const options: string[] = [];

  if (width || height) {
    // rs:<type>:<width>:<height>:<enlarge>
    options.push(
      `rs:${resize}:${width ?? 0}:${height ?? 0}:${enlarge ? 1 : 0}`,
    );
  }

  options.push(`q:${quality}`);

  return `${IMG_URL}/${options.join("/")}/${encodeBase64Url(source)}.${format}`;
}

/**
 * The size buckets the CDN used to pre-generate. imgproxy now produces these on
 * the fly from the `original` asset, so the buckets stay as the call-site API
 * while the pre-generation pipeline becomes redundant.
 */
export type CardImageSize = "original" | "316x440" | "158x220" | "90x125";

const CARD_SIZES: Record<string, { width: number; height: number }> = {
  "316x440": { width: 316, height: 440 },
  "158x220": { width: 158, height: 220 },
  "90x125": { width: 90, height: 125 },
};

/**
 * Build a card image URL from the full-resolution source.
 *
 * `size` keeps the historic bucket names so existing callers are unchanged —
 * the dimensions requested are the same ones the CDN used to serve, so this is
 * a drop-in with no visual difference.
 */
export function getCardImgproxyUrl(
  originalSrc: string,
  size: CardImageSize | string = "original",
): string {
  // "original" (and anything unrecognized) still gets format/quality
  // normalization, just no resize.
  return getImgproxyUrl(originalSrc, CARD_SIZES[size]);
}
